import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// CONFLICT OVERRIDES -------------------------------------------------------

export interface ConflictOverride {
  id: string;
  created_at: string;
  created_by: string;
  production_id: string;
  fingerprint: string;
  reason: string;
  expires_at: string | null;
}

export function useConflictOverrides(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['overrides', productionId],
    queryFn: async (): Promise<ConflictOverride[]> => {
      const { data, error } = await supabase
        .from('conflict_overrides')
        .select('*')
        .eq('production_id', productionId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useAcceptConflict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      production_id: string;
      fingerprint: string;
      reason: string;
      expires_at?: string | null;
    }) => {
      const userResp = await supabase.auth.getUser();
      const userId = userResp.data.user?.id;
      if (!userId) throw new Error('Not authenticated');
      const { error } = await supabase.from('conflict_overrides').upsert(
        {
          production_id: args.production_id,
          fingerprint: args.fingerprint,
          reason: args.reason,
          expires_at: args.expires_at ?? null,
          created_by: userId,
        },
        { onConflict: 'production_id,fingerprint' },
      );
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['overrides', args.production_id] });
    },
  });
}

export function useRevokeConflictOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; production_id: string }) => {
      const { error } = await supabase.from('conflict_overrides').delete().eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: (_, args) => {
      void qc.invalidateQueries({ queryKey: ['overrides', args.production_id] });
    },
  });
}

// LINEUP UNDO --------------------------------------------------------------

export interface LineupRemoval {
  id: string;
  removed_at: string;
  removed_by: string | null;
  show_id: string;
  dance_id: string;
  sequence_index: number;
  restored: boolean;
}

export function useRecentLineupRemovals(showId: string | undefined) {
  return useQuery({
    enabled: !!showId,
    queryKey: ['removals', showId],
    queryFn: async (): Promise<LineupRemoval[]> => {
      const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('lineup_removals')
        .select('*')
        .eq('show_id', showId!)
        .eq('restored', false)
        .gte('removed_at', cutoff)
        .order('removed_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useRestoreLastRemoval() {
  const qc = useQueryClient();
  return useMutation({
    // No auto-retry: the RPC isn't idempotent across separate invocations
    // (each call restores the *next* removal). On a network-error retry
    // the admin could silently restore a different dance than expected,
    // so failure should surface in the toast and let the admin re-click.
    retry: 0,
    mutationFn: async (showId: string): Promise<string | null> => {
      const { data, error } = await supabase.rpc('restore_last_lineup_removal', {
        p_show_id: showId,
      });
      if (error) throw error;
      return data ?? null;
    },
    onSuccess: (_, showId) => {
      void qc.invalidateQueries({ queryKey: ['lineup', showId] });
      void qc.invalidateQueries({ queryKey: ['removals', showId] });
    },
  });
}

// DANCER SCHEDULE VIEW -----------------------------------------------------

export interface DancerScheduleRow {
  dancer_id: string;
  dance_id: string;
  dance_name: string;
  length_seconds: number;
  production_id: string;
  show_id: string;
  show_name: string;
  performance_at: string;
  sequence_index: number;
  computed_start_time: string | null;
}

export function useDancerProductionSchedule(productionId: string | undefined) {
  return useQuery({
    enabled: !!productionId,
    queryKey: ['dancer-schedule', productionId],
    queryFn: async (): Promise<DancerScheduleRow[]> => {
      const { data, error } = await supabase
        .from('dancer_production_schedule')
        .select('*')
        .eq('production_id', productionId!)
        .order('performance_at', { ascending: true })
        .order('sequence_index', { ascending: true });
      if (error) throw error;
      return (data ?? []) as DancerScheduleRow[];
    },
  });
}
